// src/pages/PublicTrips.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPublicTrips, getTripPlaces } from '../services/tripService';
import './PublicTrips.css';

const PublicTrips = () => {
  const [trips, setTrips] = useState([]);
  const [tripsWithPlaces, setTripsWithPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState({
    country: '',
    search: ''
  });

  useEffect(() => {
    fetchPublicTrips();
  }, []);

  const fetchPublicTrips = async () => {
    try {
      setLoading(true);
      const publicTrips = await getPublicTrips();
      
      // 為每個行程獲取景點數據以計算總費用和評分
      const tripsWithPlacesData = await Promise.all(
        publicTrips.map(async (trip) => {
          try {
            const places = await getTripPlaces(trip.id);
            
            // 計算實際總費用
            const actualTotalCost = places.reduce((total, place) => {
              if (place.expense) {
                const placeTotal = Object.values(place.expense).reduce((sum, cost) => sum + (Number(cost) || 0), 0);
                return total + placeTotal;
              }
              return total;
            }, 0);
            
            // 計算創建者平均評分
            const ratedPlaces = places.filter(p => Number(p.rating) > 0);
            const averageRating = ratedPlaces.length > 0 
              ? (ratedPlaces.reduce((sum, p) => sum + Number(p.rating), 0) / ratedPlaces.length).toFixed(1)
              : null;
            
            return {
              ...trip,
              places,
              actualTotalCost,
              creatorAverageRating: averageRating,
              placesCount: places.length
            };
          } catch (error) {
            console.error(`Error fetching places for trip ${trip.id}:`, error);
            return {
              ...trip,
              places: [],
              actualTotalCost: 0,
              creatorAverageRating: null,
              placesCount: 0
            };
          }
        })
      );
      
      setTrips(publicTrips);
      setTripsWithPlaces(tripsWithPlacesData);
    } catch (error) {
      setError('無法載入公開行程');
      console.error('Error fetching public trips:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-TW');
  };

  const getTotalCost = (totalCost) => {
    if (!totalCost) return 0;
    if (typeof totalCost === 'object') {
      return Object.values(totalCost).reduce((sum, cost) => sum + (Number(cost) || 0), 0);
    }
    return Number(totalCost) || 0;
  };

  const getUniqueCountries = () => {
    const countries = [...new Set(tripsWithPlaces.map(trip => trip.country))];
    return countries.sort();
  };

  const filteredTrips = tripsWithPlaces.filter(trip => {
    const matchesCountry = !filter.country || trip.country === filter.country;
    const matchesSearch = !filter.search || 
      trip.title.toLowerCase().includes(filter.search.toLowerCase()) ||
      trip.city.toLowerCase().includes(filter.search.toLowerCase()) ||
      trip.country.toLowerCase().includes(filter.search.toLowerCase());
    
    return matchesCountry && matchesSearch;
  });

  if (loading) {
    return (
      <div className="public-trips-container">
        <div className="loading">載入中...</div>
      </div>
    );
  }

  return (
    <div className="public-trips-container">
      <div className="public-trips-header">
        <h1>探索公開行程</h1>
        <p>發現其他旅行者分享的精彩行程</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="filters">
        <div className="filter-group">
          <input
            type="text"
            placeholder="搜尋行程、城市或國家..."
            value={filter.search}
            onChange={(e) => setFilter(prev => ({ ...prev, search: e.target.value }))}
            className="search-input"
          />
        </div>
        
        <div className="filter-group">
          <select
            value={filter.country}
            onChange={(e) => setFilter(prev => ({ ...prev, country: e.target.value }))}
            className="country-filter"
          >
            <option value="">所有國家</option>
            {getUniqueCountries().map(country => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="trips-stats">
        <span>共找到 {filteredTrips.length} 個公開行程</span>
      </div>

      {filteredTrips.length === 0 ? (
        <div className="no-trips">
          <h3>沒有找到符合條件的行程</h3>
          <p>試試調整搜尋條件或瀏覽所有行程</p>
        </div>
      ) : (
        <div className="public-trips-grid">
          {filteredTrips.map(trip => (
            <div key={trip.id} className="public-trip-card">
              <div className="trip-card-header">
                <h3 className="trip-title">{trip.title}</h3>
                <div className="trip-location">
                  <span className="country">{trip.country}</span>
                  <span className="separator">·</span>
                  <span className="city">{trip.city}</span>
                </div>
              </div>

              <div className="trip-card-content">
                <div className="trip-dates">
                  <div className="date-item">
                    <span className="date-label">開始日期</span>
                    <span className="date-value">{formatDate(trip.start_date)}</span>
                  </div>
                  <div className="date-item">
                    <span className="date-label">結束日期</span>
                    <span className="date-value">{formatDate(trip.end_date)}</span>
                  </div>
                </div>

                <div className="trip-stats">
                  <div className="stat-item">
                    <span className="stat-label">總花費</span>
                    <span className="stat-value">
                      NT$ {(trip.actualTotalCost || getTotalCost(trip.totalCost)).toLocaleString()}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">景點數量</span>
                    <span className="stat-value">
                      {trip.placesCount} 個景點
                    </span>
                  </div>
                </div>

                <div className="trip-stats">
                  <div className="stat-item">
                    <span className="stat-label">狀態</span>
                    <span className={`stat-value status ${trip.isCompleted ? 'completed' : 'ongoing'}`}>
                      {trip.isCompleted ? '已完成' : '進行中'}
                    </span>
                  </div>
                  {trip.creatorAverageRating && (
                    <div className="stat-item">
                      <span className="stat-label">創建者評分</span>
                      <span className="stat-value creator-rating">
                        ⭐ {trip.creatorAverageRating} 星
                      </span>
                    </div>
                  )}
                </div>

                {/* 如果有行程評分，顯示其他用戶評分 */}
                {trip.tripRating && trip.tripRating.ratingCount > 0 && (
                  <div className="trip-user-rating">
                    <span className="user-rating-label">用戶評分:</span>
                    <span className="user-rating-value">
                      ⭐ {trip.tripRating.averageRating} ({trip.tripRating.ratingCount} 人評分)
                    </span>
                  </div>
                )}

                <div className="trip-meta">
                  <span className="created-date">
                    發佈於 {new Date(trip.createdAt?.seconds * 1000 || trip.created_at).toLocaleDateString('zh-TW')}
                  </span>
                </div>
              </div>

              <div className="trip-card-footer">
                <Link 
                  to={`/trip/${trip.id}`}
                  className="view-details-btn"
                >
                  查看詳情
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PublicTrips;